import { Directive, ElementRef, HostListener } from '@angular/core';

/*

    Force digits-only input (0–9) in text fields, including paste, drop, and autofill.

    How it works:
    Blocks non-digit key presses but allows navigation/shortcuts; sanitizes on every input event to strip any non-digits; supports numpad.
    When to use: fields that must store raw numeric strings (e.g., unformatted phone numbers).

    Usage:
    On a native input: <input type="text" onlyNumbers />
    On a wrapper that emits input events (like your filter box): <text-filter-box onlyNumbers></text-filter-box>
    Notes

    No formatting: it removes spaces, hyphens, parentheses, etc.; it does not add masks.
    No length limit: combine with maxlength or a validator if needed.
    Works with [(ngModel)] and reactive forms: the model receives the sanitized digits-only value.

*/

@Directive({ selector: '[onlyNumbers]' })
export class OnlyNumbersDirective {
  private allowedKeys = [
    'Backspace', 'Tab', 'Enter',
    'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown',
    'Delete', 'Home', 'End',
  ];

  constructor(private elementRef: ElementRef<HTMLInputElement>) {}

  @HostListener('keydown', ['$event'])
  onKeyDown(event: KeyboardEvent) {

    // Allow common shortcuts (copy/paste/cut/select-all/undo/redo)
    if (event.ctrlKey || event.metaKey) {
      return;
    }

    // Allow navigation/edit keys
    if (this.allowedKeys.indexOf(event.key) !== -1) {
      return;
    }

    // Allow digits (including numpad)
    if (/^[0-9]$/.test(event.key) || /^Numpad[0-9]$/.test(event.key)) {
      return;
    }

    event.preventDefault();
  }

  @HostListener('input', ['$event'])
  onInput(event: Event) {
    const target = event.target as HTMLInputElement | null;

    if (!target || typeof target.value !== 'string') { return; }
    const original = target.value || '';
    const sanitized = original.replace(/\D+/g, '');

    if (sanitized !== original) {
      const start = target.selectionStart || 0;
      const delta = original.length - sanitized.length;
      target.value = sanitized;
      target.dispatchEvent(new Event('input', { bubbles: true }));
      try {
        const newPos = Math.max(0, start - delta);
        target.setSelectionRange(newPos, newPos);
      } catch { /* ignore */ }
    }
  }
}
